import {debug} from '@/shared/helpers/debug';
import {handleBack} from '@/shared/composables/useBackHandler';
import {App} from '@capacitor/app';
import {Browser} from '@capacitor/browser';
import type {Plugin} from 'vue';
import {useRouter} from 'vue-router';
import {getAvailableLanguages} from '@/stores/localeStore';

function getDateString() {
    return new Date().toDateString();
}

/**
 * Configures handling of hybrid app specific global functionality:
 * - Handling of authentication redirects (MSAL and OIDC)
 * - Handling of universal/app links (https deep links)
 * - Hardware back button: closes overlays before navigating back
 * - Day change detection: reloads the page when the app resumes on a new day
 *
 * Supported URL formats:
 * - Custom scheme MSAL: {appId}://msauth#code=...
 * - Custom scheme OIDC: {appId}://login?code=...
 * - Universal link MSAL callback: https://app.desk.ly/#code=...
 */
export const hybridAppPlugin = {
    install(app) {
        if (!__IS_HYBRID_APP__) {
            return;
        }
        app.runWithContext(() => {
            const router = useRouter();

            let lastActiveDate = getDateString();

            App.addListener('appStateChange', ({isActive}) => {
                if (!isActive) {
                    return;
                }

                const currentDate = getDateString();
                if (currentDate !== lastActiveDate) {
                    debug.log('Day changed since last active, reloading page');
                    lastActiveDate = currentDate;
                    window.location.reload();
                }
            });

            App.addListener('backButton', () => {
                if (handleBack()) {
                    return;
                }

                if (router.options.history.state?.back) {
                    router.back();
                }
            });

            App.addListener('appUrlOpen', async data => {
                debug.log('App opened with URL:', data.url);

                // Check for custom scheme MSAL redirect: {appId}://msauth#code=...
                if (data.url.includes('://msauth')) {
                    await Browser.close();
                    return handleMsalAuthenticationRedirect(data.url);
                }

                if (data.url.includes('://login')) {
                    await Browser.close();
                    return handleOidcAuthenticationRedirect(data.url);
                }

                if (data.url.startsWith('https://')) {
                    const parsedUrl = new URL(data.url);
                    // Detect MSAL callback: hash contains code= (authorization code flow)
                    if (parsedUrl.hash.includes('code=')) {
                        // await Browser.close();
                        return handleMsalAuthenticationRedirect(data.url);
                    }
                    handleUniversalLink(data.url);
                }
            });

            function handleMsalAuthenticationRedirect(url: string) {
                const code = url.split('#')[1];
                debug.log('Extracted code from URL:', code);

                router.push({name: 'Home', hash: '#' + code});
            }

            function handleOidcAuthenticationRedirect(url: string) {
                const parsedUrl = new URL(url);
                const queryString = parsedUrl.search;

                debug.log('Redirecting to callback with query string:', queryString);

                const resolved = router.resolve({name: 'CallBack'});
                router.push(resolved.path + queryString);
            }

            function handleUniversalLink(url: string) {
                const parsedUrl = new URL(url);
                const pathname = parsedUrl.pathname;

                // Strip locale prefix (e.g. /de/, /en/) if present
                const localePattern = getAvailableLanguages().join('|');
                const strippedPath = pathname.replace(
                    new RegExp(`^\\/(${localePattern})(?=\\/)`),
                    ''
                );
                const destination = strippedPath + parsedUrl.search + parsedUrl.hash;

                const resolved = router.resolve(destination);
                if (!resolved.matched.length || resolved.name === 'NotFound') {
                    debug.log('Universal link did not match any route, ignoring:', destination);
                    return;
                }

                debug.log('Navigating to universal link path:', destination);
                router.push(resolved);
            }
        });
    },
} satisfies Plugin;
